import { Button } from '@mantine/core'
import { useRouter } from 'next/router'
import React from 'react'

import {
  useMutationCreateUserAddress,
  useMutationUpdateUserAddress,
  useQueryGetUserAddressById,
} from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUserAddress, type ParameterID } from '@flecto/types'
import { Drawer } from '@flecto/ui'

import { useAddressDrawer } from '../../hooks/useAddressDrawer'
import { AddressForm } from '../AddressForm/AddressForm'

type AddressDrawerProps = {
  addressId?: ParameterID
  onSuccess?: (address: CoreUserAddress) => void
}

export function AddressDrawer({ addressId, onSuccess }: AddressDrawerProps) {
  const router = useRouter()
  const { t } = useTranslation('saas', { keyPrefix: 'profile_locations' })
  const { data } = useQueryGetUserAddressById({ addressId: addressId ?? '' })
  const { isLoading: isLoadingCreate } = useMutationCreateUserAddress()
  const { isLoading: isLoadingUpdate } = useMutationUpdateUserAddress()
  const { closeDrawer } = useAddressDrawer()

  const title = addressId ? t('title_update') : t('title_create')

  React.useEffect(() => {
    router.events.on('routeChangeStart', closeDrawer)
    return () => {
      router.events.off('routeChangeStart', closeDrawer)
    }
  }, [])

  return (
    <Drawer withContainer>
      <Drawer.Close onClick={() => closeDrawer()} />
      <Drawer.Title>{title}</Drawer.Title>
      <Drawer.Actions>
        <Button
          type='submit'
          form='address-form'
          loading={isLoadingCreate || isLoadingUpdate}
        >
          {t('actions.save_address')}
        </Button>
      </Drawer.Actions>
      <Drawer.Content>
        <AddressForm
          id='address-form'
          initialAddress={data as Partial<CoreUserAddress>}
          onSuccess={onSuccess}
        />
      </Drawer.Content>
    </Drawer>
  )
}
